import { supabase } from '../supabaseClient';

// Helper function to fetch all data in batches (Supabase limit is 1000 rows)
async function fetchAllInBatches(rpcName, params = {}, batchSize = 1000) {
  let allData = [];
  let start = 0;
  let hasMore = true;

  while (hasMore) {
    const { data, error } = await supabase
      .rpc(rpcName, params)
      .range(start, start + batchSize - 1);

    if (error) {
      console.error(`Error fetching batch from ${rpcName}:`, error);
      throw error;
    }

    if (data && data.length > 0) {
      allData = [...allData, ...data];
      console.log(`Loaded ${allData.length} rows from ${rpcName}...`);

      // If we got fewer rows than batch size, we've reached the end
      hasMore = data.length === batchSize;
      start += batchSize;
    } else {
      hasMore = false;
    }
  }

  return allData;
}

// Get database statistics using SQL aggregation via RPC
export async function getStats() {
  try {
    const { data, error } = await supabase.rpc('get_statistics');

    if (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }

    // The RPC returns an array with one row, extract it
    return data?.[0] || {
      uniqueorders: 0,
      uniquetiles: 0,
      uniquemodules: 0,
      uniqueledbatches: 0,
      uniquernm: 0,
      uniquebnm: 0,
      uniquegnm: 0,
      uniquermcd: 0,
      uniquegmcd: 0,
      uniquebmcd: 0,
      uniquerv: 0,
      uniquegv: 0,
      uniquebv: 0
    };
  } catch (error) {
    console.error('Stats error:', error);
    throw error;
  }
}

// Get tile prefixes with aggregated data using RPC
// Fetches ALL tile types in batches (no limit)
export async function getTilePrefixes() {
  try {
    console.log('Fetching all tile prefixes in batches...');
    const data = await fetchAllInBatches('get_tile_prefixes', { result_limit: 10000 });
    console.log(`Loaded ${data.length} tile prefixes total`);
    return data;
  } catch (error) {
    console.error('Tile prefixes error:', error);
    return [];
  }
}

// Get orders with aggregated data using RPC
// Fetches ALL orders in batches (no limit)
export async function getOrders() {
  try {
    console.log('Fetching all orders in batches...');
    const data = await fetchAllInBatches('get_order_statistics', { result_limit: 10000 });
    console.log(`Loaded ${data.length} orders total`);
    return data;
  } catch (error) {
    console.error('Orders error:', error);
    return [];
  }
}

// Get year statistics using RPC
export async function getYearStatistics() {
  try {
    const { data, error } = await supabase.rpc('get_year_statistics');

    if (error) {
      console.error('Error fetching year statistics:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Year statistics error:', error);
    return [];
  }
}

// Get LED batch statistics using RPC
// Fetches ALL LED batches in batches (no limit)
export async function getLedBatchStatistics() {
  try {
    console.log('Fetching all LED batches in batches...');
    const data = await fetchAllInBatches('get_led_batch_statistics', { result_limit: 10000 });
    console.log(`Loaded ${data.length} LED batches total`);
    return data;
  } catch (error) {
    console.error('LED batch statistics error:', error);
    return [];
  }
}

// Get tile types from ROEMainInfo.info4 using RPC
// Fetches ALL tile types in batches (no limit)
export async function getTileTypesInfo4() {
  try {
    console.log('Fetching all tile types (info4) in batches...');
    const data = await fetchAllInBatches('get_tile_types_info4', { result_limit: 10000 });
    console.log(`Loaded ${data.length} tile types (info4) total`);
    return data;
  } catch (error) {
    console.error('Tile types (info4) error:', error);
    return [];
  }
}
